import { type CSSProperties, type HTMLAttributes, type ReactNode } from "react";
import { cn } from "../../lib/cn";

type TimelineItemState = "pending" | "done" | "current";

export interface TimelineItem {
  detail?: ReactNode;
  id: string;
  label: ReactNode;
  state?: TimelineItemState;
}

export interface TimelineProps extends Omit<HTMLAttributes<HTMLOListElement>, "children"> {
  items: TimelineItem[];
  variant?: "default" | "exception";
}

const timelineStateClassNames: Record<TimelineItemState, string | undefined> = {
  pending: undefined,
  done: "ui-timeline-item-done",
  current: "ui-timeline-item-current"
};

export function Timeline({ "aria-label": ariaLabel = "Timeline", className, items, style, variant = "default", ...props }: TimelineProps) {
  const timelineStyle = {
    ...style,
    "--timeline-item-count": Math.max(items.length, 1)
  } as CSSProperties;

  return (
    <ol
      className={cn("ui-timeline", variant === "exception" && "ui-timeline-exception", className)}
      aria-label={ariaLabel}
      style={timelineStyle}
      {...props}
    >
      {items.map((item) => {
        const state = item.state ?? "pending";
        return (
          <li className={cn("ui-timeline-item", timelineStateClassNames[state])} key={item.id} aria-current={state === "current" ? "step" : undefined}>
            <span className="ui-timeline-dot" aria-hidden="true" />
            <span className="ui-timeline-copy">
              <strong>{item.label}</strong>
              {item.detail && <small>{item.detail}</small>}
            </span>
          </li>
        );
      })}
    </ol>
  );
}
